import { Home, Trophy, LogOut, MessageCircle, UserRound } from 'lucide-react';

import { NavMenu, Padel24Logo } from '../components/ui';
import { RoleSwitcher } from '../components/auth';
import { PendingActionsBell } from '../components/notifications/PendingActionsBell';
import type { PendingAction } from '../state/usePendingActions';
import type { View } from './views/types';
import type { Scope } from '../lib/auth';
import { organizationContextKey, roleContexts } from '../lib/auth/roleContexts';
import { APP_VERSION } from '../data/changelog';

import { isLiveTournamentView } from './views/types';
import { useT } from '../i18n';

export interface AppHeaderProps {
  view: any;
  navigateWithAutoPause: any;
  navPinned: any;
  setNavPinned: any;
  combinedArchive: any;
  Padel24Engine: any;
  setView: any;
  clubInfo: any;
  tournament: any;
  requireLogin: any;
  onLogout: any;
  /** Unread messages for the club (badge on the header icon). */
  unreadMessages?: number;
  /** Opens «Meldinger»; the icon is only rendered when this is given. */
  onOpenMessages?: () => void;
  // ── THE BELL — what waits for whoever runs this organization ────
  //
  // Optional as a pair, like the switcher: the header renders in contexts
  // with nothing to ask, and the bell renders nothing for an empty list.
  /** The action kinds this person may see, each with its count. */
  pendingActions?: PendingAction[];
  /** Go to the view where a kind is dealt with. */
  onPendingAction?: (view: View) => void;
  // ── THE ORGANIZATION SWITCHER (#752) ──────────────────────────────
  //
  // Optional as a group: the header renders in contexts that have no account
  // session to ask (the non-API backend, and the tests that mount it bare),
  // and the switcher renders nothing without scopes anyway.
  /** Where this account may ACT — `/auth/scopes`, never roster membership. */
  scopes?: Scope[];
  /** The organization being worked in, or null for the personal home. */
  currentTenantId?: string | null;
  /** Enter one. */
  onEnterOrganization?: (tenantId: string) => void;
  /** Leave for the personal home, when there is one to go back to. */
  onPersonalHome?: () => void;
  // ── «BYTT VISNING» (owner, 2026-10-20/21) ─────────────────────────
  //
  // The one context `/auth/scopes` does not carry, from `useRoleAvailability`.
  /** The account has a player profile — offer «Spiller». */
  hasPlayerProfile?: boolean;
}

/**
 * The narrow «Min konto» / «Logg ut»: the round 34 px shape of the messages
 * button, with the pill's control edge (`--line-strong`, #809 D1).
 * `lg:hidden` — from `lg` (1024 px) up the labelled pill is shown instead.
 * Below `sm` since #1292; up to `lg` since #1294 (owner decision 2026-11-02),
 * because two pills of ~110 px each did not fit beside an organization
 * switcher and a club name on a tablet.
 */
const HEADER_ICON_BUTTON =
  'lg:hidden flex-shrink-0 flex items-center justify-center rounded-full border border-line-strong bg-paper text-ink hover:border-ink transition-colors';

export function AppHeader({
  view,
  navigateWithAutoPause,
  navPinned,
  setNavPinned,
  combinedArchive,
  Padel24Engine,
  setView,
  clubInfo,
  tournament,
  requireLogin,
  onLogout,
  unreadMessages = 0,
  onOpenMessages,
  pendingActions,
  onPendingAction,
  scopes,
  currentTenantId = null,
  onEnterOrganization,
  onPersonalHome,
  hasPlayerProfile = false,
}: AppHeaderProps) {
  const t = useT();
  return (
    <header className="sticky top-0 z-30 border-b border-line bg-bg/90 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center justify-between gap-3 max-sm:px-3 max-sm:gap-2 max-[359px]:px-2 max-[359px]:gap-1">
        {/* PHONE WIDTH (owner decision 2026-11-02). Below `sm` the two groups
            no longer fit side by side: at 390 px the right one pushed «Logg ut»
            off screen and squeezed this one to nothing, so the club button sat
            on top of the menu. Below `sm` this group keeps its size and the
            right one gives way; every `max-sm:` class here and below is
            phone-only, so the desktop header is exactly what it was.
            BELOW 360 PX (#1292 real-browser walk) even that was not enough:
            at 320 px the switcher was squeezed to its padding and vanished.
            It now has a 26 px floor, and the room comes from here — tighter
            padding and gap — and from a one-step-smaller logo icon.
            AT EVERY WIDTH SINCE #1294 (owner decision 2026-11-02): from 640
            to 1023 px — and at 1024 with two organizations — this group was
            squeezed to 0–78 px, so the logo was crushed and the club button
            sat on the menu. It never shrinks now; the right-hand group gives
            way instead. Where the header already fitted, this group was at
            its natural width anyway, so nothing there moves. */}
        <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
          {/* Hamburger + app version stacked: the version is shown right below
                the hamburger icon in the top bar, always visible. APP_VERSION is read
                automatically so the badge updates itself on a version bump. */}
          <div className="flex flex-col items-center gap-1">
            <NavMenu
              currentView={view}
              onNavigate={navigateWithAutoPause}
              pinned={navPinned}
              onTogglePin={setNavPinned}
              archiveCount={combinedArchive.length}
              Padel24Engine={Padel24Engine}
            />
            <span
              className="font-mono text-[9px] font-bold text-ink-3 leading-none px-1.5 py-0.5 rounded-md bg-bg-soft border border-line tracking-tight"
              title={t('app.version')}
            >
              v{APP_VERSION}
            </span>
          </div>
          <button
            onClick={() => navigateWithAutoPause('home')}
            className="flex items-center min-w-0 group"
          >
            {/* THE WORDMARK FROM `lg` UP, the icon below it (#1294). The full
                logo is ~290 px wide; between 640 and 1023 px that was the room
                the organization switcher and the club name needed, and it is
                what had been crushed out of shape. Below `sm` since #1292. */}
            <span className="hidden lg:block">
              <Padel24Logo size="xl" variant="full" />
            </span>
            <span className="lg:hidden max-[359px]:hidden">
              <Padel24Logo size="lg" variant="icon" />
            </span>
            <span className="hidden max-[359px]:block">
              <Padel24Logo size="md" variant="icon" />
            </span>
          </button>
        </div>
        {/* THE RIGHT-HAND GROUP GIVES WAY, at every width (#1294): only the
            organization switcher and the club's name can get narrower, and
            each truncates with an ellipsis; every other item is
            `flex-shrink-0`, so a pill never wraps and a round button never
            turns oval. Where everything fits nothing shrinks, so a header
            that fitted before is unchanged. */}
        <div className="flex items-center gap-2 min-w-0 max-sm:gap-1">
          {/* WHICH CONTEXT THIS IS, AND HOW TO CHANGE IT (#752, owner
              2026-10-20/21): the player, or one entry per organization. Before the club button on purpose: that one opens
              this organization's SETTINGS, and «which organization am I in»
              has to be answerable before «configure it» means anything.
              Renders nothing for an account with one context or none. */}
          {scopes && onEnterOrganization && (
            <RoleSwitcher
              contexts={roleContexts({ scopes, player: hasPlayerProfile })}
              currentKey={currentTenantId ? organizationContextKey(currentTenantId) : null}
              onEnterOrganization={onEnterOrganization}
              onPlayer={onPersonalHome}
            />
          )}
          {/* Club button: name + logo (if set). Opens club settings.
                The only club display in the header — no separate hardcoded club pill. */}
          <button
            onClick={() => setView('clubSettings')}
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-bg-soft transition-colors group min-w-0 max-sm:flex-shrink-0 max-sm:px-0"
            title={
              clubInfo.name
                ? t('app.clubSettings', { club: clubInfo.name })
                : t('app.clubSettings.setup')
            }
          >
            {clubInfo.logoDataUrl ? (
              <img
                src={clubInfo.logoDataUrl}
                alt={t('app.clubLogo')}
                className="w-7 h-7 object-cover rounded-full flex-shrink-0 ring-1 ring-line"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-bg-soft flex items-center justify-center flex-shrink-0 ring-1 ring-line">
                <Home size={13} className="text-ink-3" />
              </div>
            )}
            {/* A LONG NAME TRUNCATES (#1294) rather than pushing «Logg ut» off
                the screen: «Fredrikstad og Omegn Padelklubb Avdeling Østfold
                Nord» was 437 px here and overflowed even at 1024 px. The
                button's title still carries the whole name. */}
            <span className="hidden md:flex flex-col leading-tight min-w-0">
              <span className="font-semibold text-sm leading-none truncate">
                {clubInfo.name || t('app.noClub')}
              </span>
              {clubInfo.name && clubInfo.courts > 0 && (
                <span className="font-mono text-[10px] text-ink-3 mt-0.5">
                  {t('app.courts', { count: clubInfo.courts })}
                </span>
              )}
            </span>
          </button>

          {/* WHAT WAITS FOR ME (owner, 2026-10-20). Before «Meldinger»: a
              thing that needs an answer outranks a thing that may. Absent for
              anybody the server would refuse the queue — see the component. */}
          {pendingActions && onPendingAction && (
            <PendingActionsBell actions={pendingActions} onGo={onPendingAction} />
          )}
          {onOpenMessages && (
            <button
              onClick={onOpenMessages}
              className={`relative flex items-center justify-center rounded-full border transition-colors flex-shrink-0 ${
                view === 'messages'
                  ? 'bg-ink text-white border-ink'
                  : 'bg-paper text-ink border-line hover:bg-bg-soft'
              }`}
              style={{ width: 34, height: 34 }}
              title={t('app.messages')}
              aria-label={
                unreadMessages > 0
                  ? t('app.messages.unread', { count: unreadMessages })
                  : t('app.messages')
              }
            >
              <MessageCircle size={16} />
              {unreadMessages > 0 && (
                <span
                  className="absolute font-mono font-bold rounded-full flex items-center justify-center"
                  style={{
                    top: -5,
                    right: -6,
                    minWidth: 18,
                    height: 18,
                    padding: '0 5px',
                    fontSize: 10,
                    background: 'var(--lime)',
                    color: 'var(--ink)',
                    border: '2px solid var(--bg)',
                  }}
                  data-testid="header-messages-badge"
                >
                  {unreadMessages > 99 ? '99+' : unreadMessages}
                </span>
              )}
            </button>
          )}
          {tournament && !tournament.isArchived && !isLiveTournamentView(view) && (
            <button
              onClick={() => {
                // Route to the correct view based on tournament type.
                // Previous bug: 'tournament' (the Padel24 view) was used for all types
                // → crash for americano since it reads a slot format that doesn't exist.
                const targetView =
                  tournament.type === 'americano'
                    ? 'americano'
                    : tournament.type === 'mexicano'
                      ? 'mexicano'
                      : tournament.type === 'mixicano'
                        ? 'mixicano'
                        : tournament.type === 'team_mexicano'
                          ? 'teamMexicano'
                          : tournament.type === 'mix_americano'
                            ? 'mixAmericano'
                            : tournament.type === 'club_americano'
                              ? 'clubAmericano'
                              : tournament.type === 'club_team_americano'
                                ? 'clubTeamAmericano'
                                : tournament.type === 'club_mexicano'
                                  ? 'clubMexicano'
                                  : tournament.type === 'klubbmesterskap'
                                    ? 'klubbmesterskap'
                                    : 'tournament';
                setView(targetView);
              }}
              className="btn-outline flex-shrink-0"
            >
              <Trophy size={14} /> {t('app.activeTournament')}
            </button>
          )}
          {/* THE LANGUAGE FLAG IS PARKED (#533), not deleted.
              i18n is deliberately deprioritised while the product owner
              rebuilds ownership of the product, and a flag offering a
              half-translated app is a distraction rather than a feature. The
              picker survives in account settings for the accounts named in
              `showsLanguagePicker`, and the catalogs, the ratchet and the
              recorded debt are all untouched — restoring this is deleting a
              condition, not redoing a migration.

              WHEN IT COMES BACK it belongs here rather than only in settings:
              the header is where somebody who landed in the wrong language
              looks first, and a switcher reachable only after login is no use
              to the person who cannot read the login screen. */}
          {/* «Min konto» and «Logg ut»: labelled pills from `lg` up, icon
              buttons below it, named by the same catalog keys (owner decisions
              2026-11-02: below `sm` in #1292, up to `lg` in #1294). Two
              elements rather than one restyled: `.btn-outline` is written
              after the Tailwind utilities, so its padding and its `display`
              cannot be overridden by a breakpoint class. The wrapper is
              `display: contents` from `lg` up, so the pill lays out exactly as
              it did; below `lg` the wrapper and the pill are not rendered. */}
          {requireLogin && (
            <>
              <span className="hidden lg:contents">
                <button
                  onClick={() => setView('account')}
                  className="btn-outline text-xs flex-shrink-0"
                  title={t('account.title')}
                >
                  <UserRound size={12} /> {t('account.title')}
                </button>
              </span>
              <button
                type="button"
                onClick={() => setView('account')}
                className={HEADER_ICON_BUTTON}
                style={{ width: 34, height: 34 }}
                title={t('account.title')}
                aria-label={t('account.title')}
                data-testid="header-account-icon"
              >
                <UserRound size={16} aria-hidden="true" />
              </button>
            </>
          )}
          {requireLogin && (
            <>
              <span className="hidden lg:contents">
                <button
                  onClick={onLogout}
                  className="btn-outline text-xs flex-shrink-0"
                  title={t('app.signOut')}
                >
                  <LogOut size={12} /> {t('app.signOut')}
                </button>
              </span>
              <button
                type="button"
                onClick={onLogout}
                className={HEADER_ICON_BUTTON}
                style={{ width: 34, height: 34 }}
                title={t('app.signOut')}
                aria-label={t('app.signOut')}
                data-testid="header-signout-icon"
              >
                <LogOut size={16} aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
